import { useState, useEffect, useRef } from 'react';

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WD_MIN = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const pad = (n) => String(n).padStart(2, '0');
const parseYMD = (s) => {
  const m = s && String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? { y: +m[1], mo: +m[2], d: +m[3] } : null;
};
const fmtYMD = (y, mo, d) => `${y}-${pad(mo)}-${pad(d)}`;

// A calendar date picker. `markers` = [{ date:'YYYY-MM-DD', label, ring, text }]
// outlines specific days (e.g. created/resolved) inside the grid; any date is pickable.
// `dots` = [{ date:'YYYY-MM-DD', label }] adds a small corner dot (e.g. days that
// already have a committed trip); it composes with any marker on the same day.
export const DatePicker = ({ value, onChange, markers = [], dots = [], className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const sel = parseYMD(value);
  const today = new Date();
  const [view, setView] = useState(() => sel
    ? { y: sel.y, mo: sel.mo }
    : { y: today.getFullYear(), mo: today.getMonth() + 1 });

  // Recenter on the selected month each time the popover opens
  useEffect(() => {
    if (open) {
      const s = parseYMD(value);
      if (s) setView({ y: s.y, mo: s.mo });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const prevMonth = () => setView(v => (v.mo === 1 ? { y: v.y - 1, mo: 12 } : { y: v.y, mo: v.mo - 1 }));
  const nextMonth = () => setView(v => (v.mo === 12 ? { y: v.y + 1, mo: 1 } : { y: v.y, mo: v.mo + 1 }));

  const daysInMonth = new Date(view.y, view.mo, 0).getDate();
  const firstDow = new Date(view.y, view.mo - 1, 1).getDay();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const todayYMD = fmtYMD(today.getFullYear(), today.getMonth() + 1, today.getDate());
  const triggerLabel = sel
    ? `${WD[new Date(sel.y, sel.mo - 1, sel.d).getDay()]} ${sel.mo}/${sel.d}/${String(sel.y).slice(2)}`
    : 'Pick date';

  const pick = (d) => { onChange(fmtYMD(view.y, view.mo, d)); setOpen(false); };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="h-9 sm:h-8 w-[140px] px-2 rounded-md border border-input bg-background text-xs text-left hover:bg-accent flex items-center justify-between"
        title="Trip day — pick any date; created/resolved are highlighted"
      >
        <span>{triggerLabel}</span>
        <span className="text-muted-foreground">▾</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 w-64 rounded-md border border-border bg-card text-card-foreground shadow-lg p-2 text-xs">
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={prevMonth} className="h-6 w-6 rounded hover:bg-accent flex items-center justify-center">‹</button>
            <span className="font-semibold">{MONTHS[view.mo - 1]} {view.y}</span>
            <button type="button" onClick={nextMonth} className="h-6 w-6 rounded hover:bg-accent flex items-center justify-center">›</button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 mb-1 text-center text-muted-foreground">
            {WD_MIN.map((w, i) => <div key={i} className="h-5 flex items-center justify-center">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d, i) => {
              if (d === null) return <div key={i} />;
              const ymd = fmtYMD(view.y, view.mo, d);
              const isSel = ymd === value;
              const mk = markers.find(m => m.date === ymd);
              const dot = dots.find(m => m.date === ymd);
              const isToday = ymd === todayYMD;
              let cls = 'relative h-8 rounded flex items-center justify-center cursor-pointer ';
              if (mk) cls += `${mk.ring} `;                 // colored outline stays even when selected
              if (isSel) cls += 'bg-primary text-primary-foreground font-semibold ';
              else if (mk) cls += `${mk.text} `;
              else cls += 'hover:bg-accent ';
              if (isToday && !isSel) cls += 'underline underline-offset-2 ';
              const title = [mk && `${mk.label} (${ymd})`, dot && `Trip: ${dot.label}`].filter(Boolean).join(' · ') || ymd;
              return (
                <button key={i} type="button" onClick={() => pick(d)} className={cls} title={title}>
                  {d}
                  {dot && <span className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full ${isSel ? 'bg-primary-foreground' : 'bg-amber-400'}`} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
